import type { TFunction } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
	expiresAt: string
	onExpired: () => void
}

type Precision = 'days' | 'hours' | 'minutes' | 'live'

function getPrecision(msRemaining: number): Precision {
	if (msRemaining > 24 * 60 * 60 * 1000) { return 'days' }
	if (msRemaining > 60 * 60 * 1000) { return 'hours' }
	if (msRemaining > 10 * 60 * 1000) { return 'minutes' }
	return 'live'
}

function formatCountdown(msRemaining: number, precision: Precision, t: TFunction<'booking'>): string {
	if (precision === 'days') {
		const days = Math.ceil(msRemaining / (24 * 60 * 60 * 1000))
		return t('countdown.days', { count: days })
	}
	if (precision === 'hours') {
		const hours = Math.ceil(msRemaining / (60 * 60 * 1000))
		return t('countdown.hours', { count: hours })
	}
	if (precision === 'minutes') {
		const minutes = Math.ceil(msRemaining / (60 * 1000))
		return t('countdown.minutes', { count: minutes })
	}
	// live ticking M:SS
	const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000))
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return t('countdown.live', { time: `${minutes}:${String(seconds).padStart(2, '0')}` })
}

export function BookingExpiryCountdown({ expiresAt, onExpired }: Props) {
	const { t } = useTranslation('booking')
	const [msRemaining, setMsRemaining] = useState(() => new Date(expiresAt).getTime() - Date.now())
	const expiredFired = useRef(false)

	function fireExpired() {
		if (!expiredFired.current) {
			expiredFired.current = true
			onExpired()
		}
	}

	useEffect(() => {
		if (msRemaining <= 0) {
			fireExpired()
			return
		}

		const precision = getPrecision(msRemaining)
		const interval = precision === 'live' ? 1000 : 60_000

		const timer = setInterval(() => {
			const remaining = new Date(expiresAt).getTime() - Date.now()
			setMsRemaining(remaining)
			if (remaining <= 0) {
				clearInterval(timer)
				fireExpired()
			}
		}, interval)

		return () => clearInterval(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expiresAt, msRemaining])

	if (msRemaining <= 0) { return null }

	const precision = getPrecision(msRemaining)
	const label = formatCountdown(msRemaining, precision, t)
	const isUrgent = precision === 'live'

	return (
		<span className={`text-sm font-medium tabular-nums ${isUrgent ? 'text-amber-600' : 'text-gray-400'}`}>
			{label}
		</span>
	)
}
